import { useState } from "react";
import { callFunction } from "../api/callFunction";
import { showToast } from "../utils/showToast";
import { VARIANT_ID } from "./utils/listingVariants";

/**
 * Unlink the second listing from the Meetup and/or Eventbrite listing it was
 * published as, for when the author deleted that listing on the site itself.
 *
 * The ids are server-owned (`listingSyncState`), so this goes through the
 * recreateListingVariant callable rather than the form: the form never writes
 * them, and clearing an id alone would not queue the sync that makes the new
 * listing. The event doc stream brings the cleared ids back into the panel, and
 * the new ones once the sync has run.
 *
 * `republish` false only unlinks, for a listing that is switched off.
 *
 * Resolves to the callable's answer, or null when it failed (already toasted).
 */
export const useRecreateListing = (eventId) => {
  const [recreatingListing, setRecreatingListing] = useState(false);

  const recreateListing = async (platforms, { republish = true } = {}) => {
    if (recreatingListing || !eventId) return null;
    setRecreatingListing(true);
    try {
      return await callFunction("recreateListingVariant", {
        eventId,
        variantId: VARIANT_ID,
        platforms,
        republish,
      });
    } catch (error) {
      console.error("Error recreating second listing:", error);
      showToast(error?.message || "Could not unlink the listing.", { type: "error" });
      return null;
    } finally {
      setRecreatingListing(false);
    }
  };

  return { recreateListing, recreatingListing };
};
